import type { TaskListItemDTO } from "../dto";

/**
 * Task Statistics
 * Computed statistics from a collection of tasks
 */
export interface TaskStats {
    total: number;
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    completionRate: string; // Formatted percentage
}

/**
 * Task Statistics Utility
 * Pure functions for calculating task statistics
 * This keeps business logic out of the UI layer
 */
export class TaskStatsCalculator {
    /**
     * Calculate statistics from an array of tasks
     * @param tasks - Array of task DTOs
     * @param totalOverride - Optional total count override (useful when only a subset of tasks is provided)
     * @returns Computed statistics
     */
    static calculate(tasks: TaskListItemDTO[], totalOverride?: number): TaskStats {
        const total = totalOverride ?? tasks.length;
        const pending = tasks.filter(t => t.status === 'pending').length;
        const processing = tasks.filter(t => t.status === 'processing').length;
        const completed = tasks.filter(t => t.status === 'completed').length;
        const failed = tasks.filter(t => t.status === 'failed').length;

        const completionRate = total > 0
            ? ((completed / total) * 100).toFixed(1)
            : '0';

        return {
            total,
            pending,
            processing,
            completed,
            failed,
            completionRate,
        };
    }

    /**
     * Get tasks by status
     * @param tasks - Array of task DTOs
     * @param status - Status to filter by
     * @returns Filtered tasks
     */
    static filterByStatus(
        tasks: TaskListItemDTO[],
        status: 'pending' | 'processing' | 'completed' | 'failed'
    ): TaskListItemDTO[] {
        return tasks.filter(t => t.status === status);
    }

    /**
     * Get failed tasks count
     * @param tasks - Array of task DTOs
     * @returns Number of failed tasks
     */
    static getFailedCount(tasks: TaskListItemDTO[]): number {
        return tasks.filter(t => t.status === 'failed').length;
    }

    /**
     * Check if any tasks are in progress
     * @param tasks - Array of task DTOs
     * @returns True if any tasks are processing or pending
     */
    static hasActiveTasks(tasks: TaskListItemDTO[]): boolean {
        return tasks.some(t => t.status === 'processing' || t.status === 'pending');
    }

    /**
     * Get most recent tasks
     * @param tasks - Array of task DTOs
     * @param limit - Number of tasks to return
     * @returns Most recent tasks sorted by updatedAt
     */
    static getMostRecent(tasks: TaskListItemDTO[], limit: number): TaskListItemDTO[] {
        return [...tasks]
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
            .slice(0, limit);
    }
}
